import type {
  GQLChannelConsent,
  GQLConsentState,
  GQLContactConsent,
} from '../../../../hooks/graphql/contacts/useContact';
import { mapConsentEvent, type ResolvedUser } from './mapConsentHistory';
import type { ChannelConsentSummary, ConsentChannel, ConsentStatus } from './consent.types';

/**
 * DNC stays distinct from a plain opt-out. Both stop outreach, but only DNC
 * outranks the per-channel columns, so collapsing them would let the card offer
 * a toggle that cannot take effect.
 */
const STATUS_BY_STATE: Record<GQLConsentState, ConsentStatus> = {
  OPTED_IN: 'opted_in',
  OPTED_OUT: 'opted_out',
  DNC: 'dnc',
  UNKNOWN: 'unknown',
};

/**
 * Whether a per-channel column produced this standing, i.e. `call_opt_in`,
 * `sms_opt_out` and friends — as opposed to a `phone_status`/`email_status`
 * suppression or the legacy global `opt_in`, neither of which any consent event
 * authored. Only an explicit decision has a ledger row behind it, so only it can
 * be dated or attributed from the ledger. The bare `opt_in` does not match:
 * there is no separator before `opt`.
 */
function isExplicitChannelDecision(sourceField: string | null | undefined): boolean {
  return !!sourceField && /_opt_(in|out)$/.test(sourceField);
}

/**
 * When the current state was set — the date behind the card's Granted/Revoked
 * line.
 *
 * `recordedAt` is one field fed by several columns, so it can't be read as one
 * date. An opt-out stamps `{channel}_opt_out_at`; an opt-in has no column of its
 * own; and both legacy branches report the global `opt_in_date` whichever way
 * the flag points. That last case is the trap: `opt_in_date` records the day
 * consent was *given*, so a legacy `opt_in = FALSE` contact would otherwise
 * render "Revoked" against the date they opted in. It dates a grant and nothing
 * else.
 *
 * The ledger is consulted only for an explicit per-channel decision. A status
 * suppression has no transition timestamp anywhere, so falling back to the last
 * consent event there would date the standing by an unrelated older event.
 */
function resolveChangedAt(
  data: GQLChannelConsent | null | undefined,
  status: ConsentStatus,
  explicitDecision: boolean,
  ledgerOccurredAt: string | null | undefined
): string | null {
  if (!explicitDecision) {
    // Legacy rows keep no revoke timestamp, and a status suppression keeps no
    // timestamp at all — the standing is undated rather than wrongly dated.
    return status === 'opted_in' ? data?.recordedAt ?? null : null;
  }
  // A channel set on its own is dated by the ledger's last opt-in; `recordedAt`
  // would hand back the unrelated global date.
  if (status === 'opted_in') {
    return data?.lastConsentedAt ?? data?.recordedAt ?? ledgerOccurredAt ?? null;
  }
  return data?.recordedAt ?? ledgerOccurredAt ?? null;
}

function toSummary(
  channel: ConsentChannel,
  data: GQLChannelConsent | null | undefined,
  actors: Record<string, ResolvedUser>
): ChannelConsentSummary {
  const status = STATUS_BY_STATE[data?.state ?? 'UNKNOWN'] ?? 'unknown';

  // The resolver names the column it derived from; the legacy global flag means
  // no per-channel decision has been made yet.
  const inheritedFromGlobalOptIn = data?.sourceField === 'opt_in';
  const explicitDecision = isExplicitChannelDecision(data?.sourceField);

  // Attribution is only meaningful for a decision the ledger actually recorded.
  const lastEvent =
    explicitDecision && data?.lastEvent ? mapConsentEvent(data.lastEvent, actors) : null;

  return {
    channel,
    status,
    changedAt: resolveChangedAt(data, status, explicitDecision, lastEvent?.occurredAt),
    lastConsentedAt: data?.lastConsentedAt ?? null,
    lastEvent,
    inheritedFromGlobalOptIn,
    // A DNC suppression outranks every per-channel column, so it is the one
    // standing this surface cannot change.
    grantable: status !== 'dnc',
  };
}

/**
 * Which consent snapshot a contact-detail surface should render.
 *
 * The Consent tab owns the write and refetches its own query. Both queries key
 * the same normalized `Contact` entity, so that refetch also refreshes the
 * detail query the parent prop comes from. What it cannot refresh is
 * `useContactData`'s local copy, which re-syncs only when the contact id
 * changes and short-circuits back to the same object when the prop already
 * carries full data — so a snapshot taken before a toggle survives the rest of
 * the session and the rail keeps rendering the old standing while the tab shows
 * the new one.
 *
 * Consent is server-owned and read-only outside the tab, so the live prop wins.
 * The snapshot stays as the fallback for callers that pass a bare `{ id }`.
 */
export function resolveLiveConsent(
  live: { consent?: GQLContactConsent | null } | null | undefined,
  snapshot: { consent?: GQLContactConsent | null } | null | undefined
): GQLContactConsent | null {
  return live?.consent ?? snapshot?.consent ?? null;
}

/** Card order matches the design: Phone Calls, Email, SMS. */
export function mapContactConsent(
  consent: GQLContactConsent | null | undefined,
  actors: Record<string, ResolvedUser> = {}
): ChannelConsentSummary[] {
  if (!consent) return [];
  return [
    toSummary('phone', consent.call, actors),
    toSummary('email', consent.email, actors),
    toSummary('sms', consent.sms, actors),
  ];
}
